<template>
	<div class="min-vh-100 pb-5">
		<div class="" v-if="event">
			<p v-if="event.client.contact_astreinte" class="mb-0"><b>Contact d'astreinte : </b>{{ event.client.contact_astreinte }}</p>
			<p v-if="event.client.consignes" class="mb-0"><b>Consignes particulières : </b>{{ event.client.consignes }}</p>
			
			<div class="font-size-h4 mt-2 font-weight-bold">{{$t('Temporary worker information')}}</div>
			
			<form @submit.prevent="saveAndContinue">
				<div class="form-group">
					<div class="custom-control custom-checkbox">
						<input 
							id="presence-interimaire" 
							type="checkbox" 
							class="custom-control-input" 
							v-model="formData.presence_interimaire"
							@change="togglePresenceInterimaire"
						>
						<label for="presence-interimaire" class="custom-control-label">
							{{$t('Presence of temporary worker')}}
						</label>
					</div>
				</div>

				<template v-if="formData.presence_interimaire">
					<div class="form-group">
						<label for="nom-interimaire" class="form-label">{{$t('Temporary worker name')}}</label>
						<input
							type="text"
							class="form-control"
							id="nom-interimaire"
							v-model="formData.nom_interimaire"
							:placeholder="$t('Enter temporary worker name')"
							required
						>
					</div>

					<div class="form-group">
						<label for="debut-heures-interimaire" class="form-label">{{$t('Start time')}}</label>
						<input
							type="time"
							class="form-control"
							id="debut-heures-interimaire"
							v-model="formData.debut_heures_interimaire"
							required
						>
					</div>

					<div class="form-group">
						<label for="fin-heures-interimaire" class="form-label">{{$t('End time')}}</label>
						<input
							type="time"
							class="form-control"
							id="fin-heures-interimaire"
							v-model="formData.fin_heures_interimaire"
							required
						>
					</div>

					<div class="form-group">
						<label for="heures-interimaire" class="form-label">{{$t('Number of hours')}}</label>
						<input
							type="number"
							step="0.5"
							min="0"
							class="form-control"
							id="heures-interimaire"
							v-model="formData.nombre_heures_interimaire"
							:placeholder="$t('Calculated automatically')"
							readonly
							required
						>
					</div>
				</template>

				<div class="form-group">
					<label for="conducteur-aller" class="form-label">{{$t('Driver outbound trip')}}</label>
					<div class="d-flex">
						<select 
							class="form-control mr-2" 
							id="conducteur-aller"
							v-model="formData.conducteur_trajet_aller"
						>
							<option value="">{{$t('Select driver')}}</option>
							<option v-if="currentUser&&currentUser.salarieid" :value="currentUser.salarieid">{{$t('Me')}}</option>
							<option 
								v-for="conducteur in conducteurs" 
								:key="conducteur.id" 
								:value="conducteur.id"
							>
								{{ conducteur.name }}
							</option>
						</select>
						<button 
							type="button" 
							class="btn btn-outline-primary btn-sm"
							@click="setCurrentUserAsDriver('aller')"
							:title="$t('Set myself as driver')"
						>
							{{$t('Me')}}
						</button>
					</div>
				</div>

				<div class="form-group">
					<div class="custom-control custom-checkbox mb-2">
						<input 
							id="meme-conducteur" 
							type="checkbox" 
							class="custom-control-input" 
							v-model="memeConducteur"
							@change="toggleMemeConducteur"
						>
						<label for="meme-conducteur" class="custom-control-label">
							{{$t('Same driver for return trip')}}
						</label>
					</div>

					<label for="conducteur-retour" class="form-label">{{$t('Driver return trip')}}</label>
					<div class="d-flex">
						<select 
							class="form-control mr-2" 
							id="conducteur-retour"
							v-model="formData.conducteur_trajet_retour"
							:disabled="memeConducteur"
						>
							<option value="">{{$t('Select driver')}}</option>
							<option v-if="currentUser&&currentUser.salarieid" :value="currentUser.salarieid">{{$t('Me')}}</option>
							<option 
								v-for="conducteur in conducteurs" 
								:key="conducteur.id" 
								:value="conducteur.id"
							>
								{{ conducteur.name }}
							</option>
						</select>
						<button 
							type="button" 
							class="btn btn-outline-primary btn-sm"
							@click="setCurrentUserAsDriver('retour')"
							:disabled="memeConducteur"
							:title="$t('Set myself as driver')"
						>
							{{$t('Me')}}
						</button>
					</div>
				</div>

				<a href="#" @click.prevent="showRecap" class="btn btn-block btn-light mt-5">{{$t('Back')}}</a>
				<button type="submit" class="btn btn-lg btn-block btn-primary" :disabled="!isFormValid">
					{{$t('Continue to signature')}}
				</button>
			</form>
		</div>
	</div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'

export default {
	name: 'EventInterimaire',
	props: ['eventid'],
	mounted () {
		this.setPageTitle('Intérimaire - ' + this.event.client.name);
	},
	computed: {
		...mapGetters('events', ['getEvent']),
		...mapGetters('interventions', ['getIntervention']),
		...mapGetters('config', ['getConfig']),
		conducteurs() {
			const config = this.getConfig;
			return config.salaries || [];
		},
		currentUser() {
			const config = this.getConfig;
			return config.user || null;
		},
		isFormValid() {
			if (!this.formData.presence_interimaire) {
				return true;
			}
			return this.formData.nom_interimaire.trim() !== '' && 
				   this.formData.nombre_heures_interimaire !== '';
		}
	},
	watch: {
		'formData.conducteur_trajet_aller'(newVal) {
			// Si "même conducteur" est coché, synchroniser automatiquement
			if (this.memeConducteur) {
				this.formData.conducteur_trajet_retour = newVal;
			}
		},
		'formData.debut_heures_interimaire'() {
			this.calculateHeures();
		},
		'formData.fin_heures_interimaire'() {
			this.calculateHeures();
		}
	},
	beforeMount: function () {
		this.event = this.getEvent(this.eventid);
		this.intervention = this.getIntervention(this.eventid);
		
		// Initialize form with existing data
		this.formData = {
			presence_interimaire: this.intervention.interimaire.presence_interimaire,
			nom_interimaire: this.intervention.interimaire.nom_interimaire,
			nombre_heures_interimaire: this.intervention.interimaire.nombre_heures_interimaire,
			debut_heures_interimaire: this.intervention.interimaire.debut_heures_interimaire || '',
			fin_heures_interimaire: this.intervention.interimaire.fin_heures_interimaire || '',
			conducteur_trajet_aller: this.intervention.interimaire.conducteur_trajet_aller || '',
			conducteur_trajet_retour: this.intervention.interimaire.conducteur_trajet_retour || '',
		};
	},
	data: function () {
		return {
			event: {},
			intervention: {},
			memeConducteur: false,
			formData: {
				presence_interimaire: false,
				nom_interimaire: '',
				nombre_heures_interimaire: '',
				debut_heures_interimaire: '',
				fin_heures_interimaire: '',
				conducteur_trajet_aller: '',
				conducteur_trajet_retour: '',
			}
		}
	},
	methods: {
		...mapActions('interventions', ['saveInterimaire']),
		...mapActions('config', ['setPageTitle']),

		calculateHeures() {
			if (this.formData.debut_heures_interimaire && this.formData.fin_heures_interimaire) {
				const [debutHeure, debutMinute] = this.formData.debut_heures_interimaire.split(':').map(Number);
				const [finHeure, finMinute] = this.formData.fin_heures_interimaire.split(':').map(Number);

				const debutMinutes = debutHeure * 60 + debutMinute;
				const finMinutes = finHeure * 60 + finMinute;

				let diffMinutes = finMinutes - debutMinutes;

				// Handle case where end time is before start time (next day)
				if (diffMinutes < 0) {
					diffMinutes += 24 * 60;
				}

				const heures = diffMinutes / 60;
				this.formData.nombre_heures_interimaire = Math.round(heures * 100) / 100;
			}
		},

		togglePresenceInterimaire() {
			if (!this.formData.presence_interimaire) {
				// Reset fields if no temporary worker
				this.formData.nom_interimaire = '';
				this.formData.nombre_heures_interimaire = '';
				this.formData.debut_heures_interimaire = '';
				this.formData.fin_heures_interimaire = '';
			}
		},
		
		toggleMemeConducteur() {
			if (this.memeConducteur) {
				// Copy driver from outbound to return
				this.formData.conducteur_trajet_retour = this.formData.conducteur_trajet_aller;
			} else {
				// Clear return driver
				this.formData.conducteur_trajet_retour = '';
			}
		},
		
		setCurrentUserAsDriver(type) {
			const currentUser = this.currentUser;
			if (currentUser && currentUser.salarieid) {
				if (type === 'aller') {
					this.formData.conducteur_trajet_aller = currentUser.salarieid;
					// Si "même conducteur" est coché, synchroniser le retour
					if (this.memeConducteur) {
						this.formData.conducteur_trajet_retour = currentUser.salarieid;
					}
				} else if (type === 'retour') {
					this.formData.conducteur_trajet_retour = currentUser.salarieid;
				}
			}
		},
		
		showRecap() {
			this.$router.push({name: 'eventRecap', params:{eventid: this.eventid}});
		},
		
		showSignature() {
			this.$router.push({name: 'eventSignatureClient', params:{eventid: this.eventid}});
		},
		
		saveAndContinue() {
			// Save data to store
			this.saveInterimaire({
				uid: this.eventid, 
				data: this.formData
			});
			
			// Continue to signature
			this.showSignature();
		}
	}
}
</script>

<style scoped>
.form-label {
	font-weight: bold;
	margin-bottom: 0.5rem;
}
</style>